import {AgentContext} from 'common/src/modules/shared/copilot/copilot.types';
import {CLStore} from 'common/src/redux/store';
import {
  ContentDraftItem,
  ContentFeed,
  ColumnDataType,
  ColumnPurpose,
  ContentFeedColumnDefinition,
  ContentFeedType,
} from 'common/src/modules/contentFeeds/contentFeeds.types';
import {RemoteAiContentContentFeedColumnParams} from 'common/src/modules/contentFeeds/contentFeeds.aiContentTypes';
import {DynamicParametersType} from 'common/src/modules/dynamicParameters/dynamicParameters.enums';
import {getGenerationPipelineDescriptor} from '../utils/generationPipelineDescriptors';
import {
  CreativeTemplate,
  CreativeTemplateType,
  templateTypeNamesByValue,
} from 'common/src/modules/creativeTemplates/creativeTemplates.types';
import {Persona} from 'common/src/modules/brandGuidelines/brandGuidelines.types';
import {taxonomy} from '@cl/shared';
import taxonomize from 'common/src/modules/taxonomy/utils/taxonomize';
import {TaxonomyType} from 'common/src/modules/taxonomy/taxonomy.enums';
import routingService from 'common/src/modules/routing/routing.service';
import {RouteKey} from 'common/src/modules/routing/routing.enums';

type PersonaInfo = {id: number; name: string | null};

type ContentFeedAiGeneratedContextData = {
  feed: ContentFeed;
  referenceTemplate: CreativeTemplate | null;
  draftItems: ContentDraftItem[];
  projectPersonas: PersonaInfo[];
  currentStep: string;
};

const ISSUE_LEVEL_ERROR = 0;

const DYNAMIC_PARAM_TYPE_LABELS: Partial<Record<DynamicParametersType, string>> = {
  [DynamicParametersType.Text]: 'Text',
  [DynamicParametersType.Color]: 'Color',
  [DynamicParametersType.FloatNumber]: 'Number',
  [DynamicParametersType.Image]: 'Image',
  [DynamicParametersType.Video]: 'Video',
  [DynamicParametersType.Boolean]: 'Boolean',
  [DynamicParametersType.Selector]: 'Selector',
  [DynamicParametersType.Audio]: 'Audio',
  [DynamicParametersType.Link]: 'Link',
  [DynamicParametersType.Widget]: 'Widget',
  [DynamicParametersType.Font]: 'Font',
  [DynamicParametersType.QR]: 'QR',
  [DynamicParametersType.Subtitles]: 'Subtitles',
};

const COLUMN_TYPE_LABELS: Record<ColumnDataType, string> = {
  [ColumnDataType.Text]: 'Text',
  [ColumnDataType.Number]: 'Number',
  [ColumnDataType.Color]: 'Color',
  [ColumnDataType.Image]: 'Image',
  [ColumnDataType.Video]: 'Video',
  [ColumnDataType.Persona]: 'Persona',
  [ColumnDataType.Language]: 'Language',
};

function csvEscape(value: string): string {
  if (value.includes(',') || value.includes('"')) {
    return `"${value.replace(/"/g, '""')}"`;
  }

  return value;
}

function getStepLabel(routeKey: RouteKey | undefined): string {
  switch (routeKey) {
    case RouteKey.ContentFeedPage:
      return 'Homepage';
    case RouteKey.ContentFeedAiGenerationSetup:
      return 'Column setup (AI generation)';
    case RouteKey.ContentFeedDraftItems:
      return 'Draft items';
    case RouteKey.ContentFeedItems:
      return 'Items (published inventory)';
    case RouteKey.ContentFeedMapping:
      return 'Mapping';
    case RouteKey.ContentFeedSets:
      return 'Content sets';
    default:
      return routeKey ? String(routeKey) : 'Unknown';
  }
}

function formatBriefSection(feed: ContentFeed, projectPersonas: PersonaInfo[]): string {
  const brief = feed.brief;
  const lines: string[] = ['### Feed brief (for AI generation)'];

  lines.push(`- Objective: ${brief?.objective || 'Not set'}`);
  lines.push(`- Description: ${brief?.description || 'Not set'}`);
  lines.push(`- Apply brand guidelines: ${brief?.applyBrandGuidelines ? 'Yes' : 'No'}`);

  if (projectPersonas.length > 0) {
    const personaList = projectPersonas
      .map((p) => `${p.name || 'Unnamed'} (ID: ${p.id})`)
      .join(', ');
    lines.push(`- Personas: ${projectPersonas.length} assigned — ${personaList}`);
  } else {
    lines.push('- Personas: None assigned');
  }

  return lines.join('\n');
}

function formatFeedColumnsSection(feed: ContentFeed): string {
  const columns = feed.feedColumnsSchema?.columns ?? [];

  if (columns.length === 0) {
    return '### Feed column definitions\nNo columns defined.';
  }

  const taxonomyMapping = taxonomy.taxonomyMappingsSubject.data?.[TaxonomyType.Content] || {};
  const lines: string[] = ['### Feed column definitions (CSV)'];
  lines.push('#,columnKey,Display name,Type,Purpose,AI Generation setup');

  columns.forEach((col: ContentFeedColumnDefinition, index: number) => {
    const typeName = COLUMN_TYPE_LABELS[col.type] || 'Unknown';
    const friendly = taxonomize(taxonomyMapping, col.columnKey, col.columnKey);
    const purpose = col.purpose != null ? String(col.purpose) : '';
    const descriptor = getGenerationPipelineDescriptor(col.aiGenerationSetup);
    const setup = csvEscape(descriptor?.formatSummary() || 'Not configured');

    lines.push(
      `${index + 1},${col.columnKey},${friendly},${typeName},${purpose || ''},${setup}`,
    );
  });

  return lines.join('\n');
}

function formatTemplateSection(feed: ContentFeed, referenceTemplate: CreativeTemplate | null): string {
  if (!referenceTemplate) {
    return '### Reference template\nNo template loaded (template may still be selected by ID on the feed).';
  }

  const typeName =
    referenceTemplate.type != null
      ? templateTypeNamesByValue[referenceTemplate.type as CreativeTemplateType]
      : 'Unknown';

  const adSizes = feed.templateMetadata?.adSizes?.join(', ') || 'None';
  const taxonomyMapping = taxonomy.taxonomyMappingsSubject.data?.[TaxonomyType.Content] || {};
  const requiredColumns = formatTemplateRequiredColumns(feed, taxonomyMapping);

  return [
    '### Reference template',
    `- Template: ${referenceTemplate.name} (ID: ${referenceTemplate.id})`,
    `- Type: ${typeName}`,
    `- Supported ad sizes: ${adSizes}`,
    requiredColumns,
  ].join('\n');
}

function formatTemplateRequiredColumns(
  feed: ContentFeed,
  taxonomyMapping: Record<string, string>,
): string {
  const columns = feed.templateMetadata?.contentFeedColumns?.columns;

  if (!columns || Object.keys(columns).length === 0) {
    return '- Columns from this content feed, required by the creative template: None';
  }

  const lines: string[] = ['- Columns required by creative template:'];

  Object.values(columns).forEach((col: RemoteAiContentContentFeedColumnParams) => {
    const columnKey = col.contentFeedColumnName;
    const displayName = taxonomize(taxonomyMapping, columnKey, columnKey);
    const typeName = DYNAMIC_PARAM_TYPE_LABELS[col.type] || 'Unknown';
    const purpose = col.purpose ? ` — ${col.purpose}` : '';
    lines.push(`  - ${displayName} (key: ${columnKey}, type: ${typeName})${purpose}`);
  });

  return lines.join('\n');
}

function formatDraftItemsSummary(feed: ContentFeed, draftItems: ContentDraftItem[]): string {
  if (draftItems.length === 0) {
    return '### Draft items summary\nNo draft items yet.';
  }

  const creativeColumnKeys = new Set(
    (feed.feedColumnsSchema?.columns ?? [])
      .filter((c) => c.purpose === ColumnPurpose.UsedInCreative)
      .map((c) => c.columnKey),
  );

  const generated: number[] = [];
  const notGenerated: number[] = [];
  const withErrors: number[] = [];

  for (const item of draftItems) {
    const creativeColData = Object.entries(item.columnValues ?? {})
      .filter(([key]) => creativeColumnKeys.has(key))
      .map(([, data]) => data);

    const hasError = creativeColData.some((col) =>
      Object.values(col?.byAdSize ?? {}).some((v) =>
        v?.generationIssues?.some((issue) => issue.level === ISSUE_LEVEL_ERROR),
      ),
    );

    const hasGenerated = creativeColData.some((col) =>
      Object.values(col?.byAdSize ?? {}).some((v) => !!v?.generationTimestamp),
    );

    if (hasError) {
      withErrors.push(item.id);
    } else if (hasGenerated) {
      generated.push(item.id);
    } else {
      notGenerated.push(item.id);
    }
  }

  const lines: string[] = ['### Draft items summary'];
  lines.push(`- Total draft items: ${draftItems.length}`);
  
  if (generated.length > 0) {
    lines.push(`  - Creative Assets Generated: ${generated.length} [IDs: ${generated.join(', ')}]`);
  }

  if (notGenerated.length > 0) {
    lines.push(`  - Creative Assets Not Generated Yet: ${notGenerated.length} [IDs: ${notGenerated.join(', ')}]`);
  }

  if (withErrors.length > 0) {
    lines.push(`  - Creative Assets Generated with Error: ${withErrors.length} [IDs: ${withErrors.join(', ')}]`);
  }

  return lines.join('\n');
}

export default function contentFeedAiGeneratedContext(
  store: CLStore,
): AgentContext<ContentFeedAiGeneratedContextData | null> {
  function getter(): ContentFeedAiGeneratedContextData | null {
    const state = store.getState();
    const feed = state.contentFeedPage?.feed ?? null;

    if (!feed || feed.type !== ContentFeedType.AiGenerated) {
      return null;
    }

    const allPersonas: Persona[] = state.brandGuidelinesPage?.brandPersonas?.data ?? [];
    const personaIds = feed.brief?.personaIds ?? [];
    const projectPersonas: PersonaInfo[] = allPersonas
      .filter((p) => personaIds.includes(p.id))
      .map((p) => ({id: p.id, name: p.name}));

    const route = routingService.getCurrentRouteWithParams();

    return {
      feed,
      referenceTemplate: state.contentFeedPage?.referenceTemplate ?? null,
      draftItems: state.contentFeedPage?.draftItems?.items ?? [],
      projectPersonas,
      currentStep: getStepLabel(route?.routeKey),
    };
  }

  function formatter({
    currentContext,
  }: {
    prevContext: ContentFeedAiGeneratedContextData | null;
    currentContext: ContentFeedAiGeneratedContextData | null;
  }): string | null {
    if (currentContext == null) {
      return null;
    }

    const {feed, referenceTemplate, draftItems, projectPersonas, currentStep} = currentContext;

    const sections = [
      `## Content feed (type:AI generated): ${feed.name} (ID: ${feed.id})`,
      '',
      `**User is currently viewing this step:** ${currentStep}`,
      '',
      formatBriefSection(feed, projectPersonas),
      '',
      formatTemplateSection(feed, referenceTemplate),
      '',
      formatFeedColumnsSection(feed),
      '',
      formatDraftItemsSummary(feed, draftItems),
    ];

    return sections.join('\n');
  }

  return {
    title: 'Content feed (AI generated)',
    getter,
    formatter,
  };
}
